// @ts-nocheck
import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/** Longest edge, in pixels, of the preview image attached to the line item. */
const PREVIEW_MAX_EDGE = 1200;

/** JPEG quality used when encoding the preview image. */
const PREVIEW_QUALITY = 0.82;

/** How long to wait for the preview canvas to finish painting, in milliseconds. */
const PREVIEW_PAINT_TIMEOUT = 1500;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  /** @type {boolean} Whether the button was already disabled before the request. */
  #wasDisabled = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Toggles the in-progress state of the button so the customer gets immediate
   * feedback while the preview image is generated and uploaded.
   *
   * @param {boolean} pending - Whether an add to cart request is in flight.
   */
  setPending(pending) {
    const { addToCartButton } = this.refs;

    if (pending) this.#wasDisabled = addToCartButton.disabled;

    addToCartButton.classList.toggle('atc-loading', pending);
    addToCartButton.setAttribute('aria-busy', pending ? 'true' : 'false');
    // Disabling blocks double submits, but never re-enable a sold out variant.
    addToCartButton.disabled = pending || this.#wasDisabled;
  }

  /**
   * Plays the "added" feedback. Called once the item is actually in the cart.
   */
  animateAddedToCart() {
    this.animateAddToCart();

    if (this.dataset.addToCartAnimation !== 'true') return;
    if (this.closest('.quick-add-modal')) return;

    this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} Guards against a second submit while one is in flight. */
  #submitting = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
   // target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    event.preventDefault();

    // Spam clicks and the Enter key can both land here while a request is open.
    if (this.#submitting) return;

    const { addToCartTextError, addToCartButtonContainer } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    // The uploaded artwork is mandatory, so bail out before any expensive work.
    const productImageUploader = document.querySelector('product-image-uploader');
    const imageUploaded = new FormData(form).get('properties[Image Uploaded]');

    if (!imageUploaded || imageUploaded === 'null' || imageUploaded === 'undefined') {
      productImageUploader?.classList.add('required');
      return;
    }

    productImageUploader?.classList.remove('required');

    // Show the pending state in the same task as the click, before we await
    // anything, so the button reacts instantly.
    this.#submitting = true;
    addToCartButtonContainer?.setPending(true);

    try {
      /** @type {File} */
      let previewFile;

      try {
        previewFile = await createPreviewFile();
      } catch (error) {
        console.error('Failed to generate canvas preview:', error);
        this.#showFormError('Failed to generate preview image. Please try again.');
        return;
      }

      const formData = new FormData(form);
      formData.set('properties[Preview Image]', previewFile);

      // Ask for every rendered cart section once. Appending inside the loop used
      // to send one `sections` entry per component, which made the server render
      // the same sections repeatedly.
      const sectionIds = new Set();

      for (const item of document.querySelectorAll('cart-items-component')) {
        if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.add(item.dataset.sectionId);
      }

      if (sectionIds.size) formData.append('sections', Array.from(sectionIds).join(','));

      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });

      const result = await response.json();

      if (result.status) {
        this.#handleAddError(form, formData, result);
        return;
      }

      const id = formData.get('id');
      if (!id) throw new Error('Form ID is required');

      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }

      // Only celebrate once the item is really in the cart.
      addToCartButtonContainer?.setPending(false);
      addToCartButtonContainer?.animateAddedToCart();

      const addToCartButton = addToCartButtonContainer?.refs.addToCartButton;

      if (addToCartButton) {
        const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
        const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

        this.#setLiveRegionText(addedText);

        setTimeout(() => {
          this.#clearLiveRegionText();
        }, 5000);
      }

      this.dispatchEvent(
        new CartAddEvent({}, id.toString(), {
          source: 'product-form-component',
          itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
          productId: this.dataset.productId,
          sections: result.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.#submitting = false;
      addToCartButtonContainer?.setPending(false);
      cartPerformance.measureFromEvent('add:user-action', event);
    }
  }

  /**
   * Shows an inline error under the add to cart button and announces it.
   *
   * @param {string} message - The message to display.
   */
  #showFormError(message) {
    const { addToCartTextError } = this.refs;

    if (!addToCartTextError) return;

    addToCartTextError.classList.remove('hidden');

    // Reuse the text node if the user is spam-clicking
    const textNode = addToCartTextError.childNodes[2];

    if (textNode) {
      textNode.textContent = message;
    } else {
      addToCartTextError.appendChild(document.createTextNode(message));
    }

    this.#setLiveRegionText(message);

    this.#timeout = setTimeout(() => {
      addToCartTextError.classList.add('hidden');
      this.#clearLiveRegionText();
    }, 10000);
  }

  /**
   * Handles an error returned by the cart/add endpoint.
   *
   * @param {HTMLFormElement} form - The product form.
   * @param {FormData} formData - The submitted form data.
   * @param {*} result - The parsed error response.
   */
  #handleAddError(form, formData, result) {
    this.dispatchEvent(
      new CartErrorEvent(form.getAttribute('id') || '', result.message, result.description, result.errors)
    );

    this.#showFormError(result.message);

    // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
    // because our back-end still adds the max allowed amount to the cart.
    this.dispatchEvent(
      new CartAddEvent({}, this.id, {
        didError: true,
        source: 'product-form-component',
        itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
        productId: this.dataset.productId,
      })
    );
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    if(event.detail.resource) {
      variantId.value = event.detail.resource.id ?? '';
    } else {
      publish(PUB_SUB_EVENTS.variantChanged, {
        data: {
          event,
        },
      });
    }


    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

/**
 * Encodes a canvas as a blob, preferring the offscreen path when it is available.
 *
 * @param {HTMLCanvasElement | OffscreenCanvas} canvas - The canvas to encode.
 * @returns {Promise<Blob | null>} The encoded image.
 */
function canvasToBlob(canvas) {
  if ('convertToBlob' in canvas) {
    return canvas.convertToBlob({ type: 'image/jpeg', quality: PREVIEW_QUALITY });
  }

  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', PREVIEW_QUALITY));
}

/**
 * The canvases the preview could be rendered from, most wanted first.
 *
 * The customer can swipe the gallery onto any thumbnail, so the active slide says where
 * they are looking rather than what they configured. The gallery resolves the mock-up
 * from the variant picker selection instead, and the active slide is kept as the fallback
 * - a product without the framing options still has one, and it is the better answer than
 * an empty canvas when the configured slide never painted.
 *
 * @param {HTMLElement | null} slide - The gallery slide the selection resolved to.
 * @returns {HTMLCanvasElement[]} The candidate canvases, without duplicates.
 */
function previewCanvasCandidates(slide) {
  const selected = slide?.querySelector('image-canvas-simulation > .paint-canvas');
  const active = document.querySelector('.swiper-slide-active > image-canvas-simulation > .paint-canvas');

  /** @type {HTMLCanvasElement[]} */
  const candidates = [];

  for (const canvas of [selected, active]) {
    if (canvas instanceof HTMLCanvasElement && !candidates.includes(canvas)) candidates.push(canvas);
  }

  return candidates;
}

/**
 * Whether the simulation has drawn the customer's artwork onto the canvas.
 *
 * The simulation only records the upload once the artwork is warped into the frame, so an
 * untouched canvas - one whose template never loaded, or that has no transparent window to
 * draw into - is recognisable before it is encoded.
 *
 * @param {HTMLCanvasElement} canvas - The canvas to check.
 * @returns {boolean} Whether the artwork is on it.
 */
function hasDrawnArtwork(canvas) {
  return Boolean(canvas.closest('image-canvas-simulation')?.uploadedImage);
}

/**
 * Waits until the canvas has drawn the uploaded artwork.
 *
 * A canvas repaints asynchronously after an upload, and the slide the selection points at
 * is not always the one on screen, so it can still be mid-paint. Waiting beats encoding a
 * blank frame; the timeout keeps a canvas that never paints - one whose template failed to
 * load, say - from holding up the add to cart.
 *
 * @param {HTMLCanvasElement} canvas - The canvas about to be encoded.
 */
async function waitForCanvasPaint(canvas) {
  const deadline = performance.now() + PREVIEW_PAINT_TIMEOUT;

  while (!hasDrawnArtwork(canvas) && performance.now() < deadline) {
    await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));
  }
}

/**
 * Names the preview after the frame it shows.
 *
 * The cart and the order show this file by its name, so it carries the orientation, style
 * and mat border the slide states - `portrait-black-frame-with-mat-preview.jpg` - rather
 * than a name every line item shares.
 *
 * @param {HTMLElement | null} slide - The gallery slide the preview was rendered from.
 * @returns {string} The file name.
 */
function previewFileName(slide) {
  const { slideOrientation, slideStyles, slideMats } = slide?.dataset ?? {};
  const parts = [slideOrientation, slideStyles, slideMats].filter((part) => part && part !== 'false');

  // A product without the framing options resolves no slide, so it keeps the plain name.
  return parts.length ? `${parts.join('-').replaceAll(' ', '-')}-preview.jpg` : 'preview-image.jpg';
}

/**
 * Renders the canvas simulation of the selected frame into the preview image attached to
 * the line item.
 *
 * The simulation canvas matches the source artwork, which is often 2000px or more on the
 * long edge. A lossless PNG of that costs several megabytes to encode and, far more
 * expensively, to upload with the cart request. The cart only ever shows this file as a
 * preview - the print-resolution artwork is the Filestack URL in
 * `properties[Image Uploaded]` - so we downscale it and encode a JPEG instead.
 *
 * @returns {Promise<File>} The preview image file.
 */
async function createPreviewFile() {
  // Let the browser paint first, so a slide that just became active is rendered.
  await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));

  const slide = document.querySelector('product-media-gallery')?.getSelectedPreviewSlide?.() ?? null;
  const candidates = previewCanvasCandidates(slide);

  if (!candidates.length) throw new Error('No preview canvas found');

  await waitForCanvasPaint(candidates[0]);

  // The point of the preview is the simulation, so never encode a canvas the artwork was
  // not drawn onto - that reaches the cart as a blank white image.
  const canvas = candidates.find(hasDrawnArtwork) ?? candidates[0];

  const scale = Math.min(1, PREVIEW_MAX_EDGE / Math.max(canvas.width, canvas.height));
  const width = Math.max(1, Math.round(canvas.width * scale));
  const height = Math.max(1, Math.round(canvas.height * scale));

  /** @type {HTMLCanvasElement | OffscreenCanvas} */
  let target;

  if (typeof OffscreenCanvas === 'function') {
    target = new OffscreenCanvas(width, height);
  } else {
    target = document.createElement('canvas');
    target.width = width;
    target.height = height;
  }

  const ctx = target.getContext('2d');

  if (!ctx) throw new Error('Preview canvas context unavailable');

  // The simulation canvas is transparent outside the frame and JPEG has no alpha,
  // so flatten it onto white rather than letting those pixels turn black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(canvas, 0, 0, width, height);

  const blob = await canvasToBlob(target);

  if (!blob) throw new Error('Preview encoding failed');

  return new File([blob], previewFileName(slide), { type: 'image/jpeg' });
}
